import { useState, useEffect } from "react";
import { 
  saveAddressDetails,
  getAllResources,
  getAllCountries,
  getRegionsByCountry,
  getDistrictsByRegion,
  getTehsilsByDistrict,
  getCitiesByTehsil
} from "../Api/AddressDetails";
import { Form, Button, Row, Col, FormCheck } from "react-bootstrap";
import { toast } from "react-toastify";
import Sample14 from "../Components/Logo";

const AddressDetails = () => {
  const [loading, setLoading] = useState(false);
  const [dropdownData, setDropdownData] = useState({
    resources: [],
    countries: [],
    regions: [],
    districts: [],
    tehsils: [],
    cities: []
  });
  
  const [isSameAddress, setIsSameAddress] = useState(false);
  
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const userId = userDetails?.userId; 

  const [addressData, setAddressData] = useState({
    resourceId: "",
    currentAddress: {
      countryCode: "",
      regionCode: "",
      districtCode: "",
      tehsilCode: "",
      cityCode: "",
      address1: "",
      address2: "",
      address3: ""
    },
    permanentAddress: {
      countryCode: "",
      regionCode: "",
      districtCode: "",
      tehsilCode: "",
      cityCode: "",
      address1: "",
      address2: "",
      address3: ""
    }
  });

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        setLoading(true);
        const [resources, countries] = await Promise.all([
          getAllResources(),
          getAllCountries()
        ]);

        setDropdownData(prev => ({
          ...prev,
          resources,
          countries
        }));
      } catch (error) {
        toast.error(`Error loading dropdown data: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchDropdownData();
  }, []);

  // Fetch regions when current country changes
  useEffect(() => {
    const fetchRegions = async () => {
      if (addressData.currentAddress.countryCode) {
        try {
          const regions = await getRegionsByCountry(addressData.currentAddress.countryCode);
          setDropdownData(prev => ({
            ...prev,
            regions
          }));
          // Reset dependent fields when country changes
          setAddressData(prev => ({
            ...prev,
            currentAddress: {
              ...prev.currentAddress,
              regionCode: "",
              districtCode: "",
              tehsilCode: "",
              cityCode: ""
            }
          }));
          setDropdownData(prev => ({
            ...prev,
            districts: [],
            tehsils: [],
            cities: []
          }));
        } catch (error) {
          toast.error("Failed to load regions!");
        }
      } else {
        setDropdownData(prev => ({
          ...prev,
          regions: [],
          districts: [],
          tehsils: [],
          cities: []
        }));
        setAddressData(prev => ({
          ...prev,
          currentAddress: {
            ...prev.currentAddress,
            regionCode: "",
            districtCode: "",
            tehsilCode: "",
            cityCode: ""
          }
        }));
      }
    };
    fetchRegions();
  }, [addressData.currentAddress.countryCode]);

  // Fetch districts when current region changes
  useEffect(() => {
    const fetchDistricts = async () => {
      if (addressData.currentAddress.regionCode) {
        try {
          // Extract region code from the selected value (format: "code - name")
          const regionCode = addressData.currentAddress.regionCode.split(" - ")[0];
          const districts = await getDistrictsByRegion(regionCode);
          setDropdownData(prev => ({
            ...prev,
            districts
          }));
          // Reset dependent fields when region changes
          setAddressData(prev => ({
            ...prev,
            currentAddress: {
              ...prev.currentAddress,
              districtCode: "",
              tehsilCode: "",
              cityCode: ""
            }
          }));
          setDropdownData(prev => ({
            ...prev,
            tehsils: [],
            cities: []
          }));
        } catch (error) {
          toast.error("Failed to load districts!");
        }
      } else {
        setDropdownData(prev => ({
          ...prev,
          districts: [],
          tehsils: [],
          cities: []
        }));
        setAddressData(prev => ({
          ...prev,
          currentAddress: {
            ...prev.currentAddress,
            districtCode: "",
            tehsilCode: "",
            cityCode: ""
          }
        }));
      }
    };
    fetchDistricts();
  }, [addressData.currentAddress.regionCode]);

  // Fetch tehsils when current district changes
  useEffect(() => {
    const fetchTehsils = async () => {
      if (addressData.currentAddress.districtCode) {
        try {
          // Extract district code from the selected value (format: "code - description")
          const districtCode = addressData.currentAddress.districtCode.split(" - ")[0];
          const tehsils = await getTehsilsByDistrict(districtCode);
          setDropdownData(prev => ({
            ...prev,
            tehsils
          }));
          // Reset dependent fields when district changes
          setAddressData(prev => ({
            ...prev,
            currentAddress: {
              ...prev.currentAddress,
              tehsilCode: "",
              cityCode: ""
            }
          }));
          setDropdownData(prev => ({
            ...prev,
            cities: []
          }));
        } catch (error) {
          toast.error("Failed to load tehsils!");
        }
      } else {
        setDropdownData(prev => ({
          ...prev,
          tehsils: [],
          cities: []
        }));
        setAddressData(prev => ({
          ...prev,
          currentAddress: {
            ...prev.currentAddress,
            tehsilCode: "",
            cityCode: ""
          }
        }));
      }
    };
    fetchTehsils();
  }, [addressData.currentAddress.districtCode]);

  // Fetch cities when current tehsil changes
  useEffect(() => {
    const fetchCities = async () => {
      if (addressData.currentAddress.tehsilCode) {
        try {
          // Extract tehsil code from the selected value (format: "code - description")
          const tehsilCode = addressData.currentAddress.tehsilCode.split(" - ")[0];
          const cities = await getCitiesByTehsil(tehsilCode);
          setDropdownData(prev => ({
            ...prev,
            cities
          }));
          // Reset city when tehsil changes
          setAddressData(prev => ({
            ...prev,
            currentAddress: {
              ...prev.currentAddress,
              cityCode: ""
            }
          }));
        } catch (error) {
          toast.error("Failed to load cities!");
        }
      } else {
        setDropdownData(prev => ({
          ...prev,
          cities: []
        }));
        setAddressData(prev => ({
          ...prev,
          currentAddress: {
            ...prev.currentAddress,
            cityCode: ""
          }
        }));
      }
    };
    fetchCities();
  }, [addressData.currentAddress.tehsilCode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle current address fields
    if (name.startsWith("currentAddress.")) {
      const field = name.split(".")[1];
      setAddressData(prev => ({
        ...prev,
        currentAddress: {
          ...prev.currentAddress,
          [field]: value
        }
      }));

      // If "same address" is checked, update permanent address too
      if (isSameAddress) {
        setAddressData(prev => ({
          ...prev,
          permanentAddress: {
            ...prev.permanentAddress,
            [field]: value
          }
        }));
      }
    } 
    // Handle permanent address fields
    else if (name.startsWith("permanentAddress.")) {
      const field = name.split(".")[1];
      setAddressData(prev => ({
        ...prev,
        permanentAddress: {
          ...prev.permanentAddress,
          [field]: value
        }
      }));
    }
    // Handle resourceId
    else {
      setAddressData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSameAddressChange = (e) => {
    const checked = e.target.checked;
    setIsSameAddress(checked);
    
    if (checked) {
      setAddressData(prev => ({
        ...prev,
        permanentAddress: { ...prev.currentAddress }
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Prepare data for API
      const apiData = {
        resourceId: addressData.resourceId,
        currentAddress: {
          ...addressData.currentAddress,
          regionCode: addressData.currentAddress.regionCode.split(" - ")[0],
          districtCode: addressData.currentAddress.districtCode.split(" - ")[0],
          tehsilCode: addressData.currentAddress.tehsilCode.split(" - ")[0],
          cityCode: addressData.currentAddress.cityCode.split(" - ")[0]
        },
        permanentAddress: {
          ...addressData.permanentAddress,
          regionCode: addressData.permanentAddress.regionCode.split(" - ")[0],
          districtCode: addressData.permanentAddress.districtCode.split(" - ")[0],
          tehsilCode: addressData.permanentAddress.tehsilCode.split(" - ")[0],
          cityCode: addressData.permanentAddress.cityCode.split(" - ")[0]
        }
      };

      await saveAddressDetails(apiData, userId);
      toast.success("Address details saved successfully!");
      
      // Reset form
      setAddressData({
        resourceId: "",
        currentAddress: {
          countryCode: "",
          regionCode: "",
          districtCode: "",
          tehsilCode: "",
          cityCode: "",
          address1: "",
          address2: "",
          address3: ""
        },
        permanentAddress: {
          countryCode: "",
          regionCode: "",
          districtCode: "",
          tehsilCode: "",
          cityCode: "",
          address1: "",
          address2: "",
          address3: ""
        }
      });
      setIsSameAddress(false);
    } catch (error) {
      toast.error(`Error: ${error.message || "Failed to save address details"}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fields-container">
      <h2 className="fields-title">Address Details</h2>

      <Form onSubmit={handleSubmit} className="fields-form">
        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Resource ID <span className="text-red">**</span></Form.Label>
              <Form.Select 
                name="resourceId" 
                value={addressData.resourceId} 
                onChange={handleChange} 
                required
              >
                <option value="">Select Resource</option>
                {dropdownData.resources.map((resource) => (
                  <option key={resource.resourceId} value={resource.resourceId}>
                    {resource.resourceId} - {resource.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <h4 className="mt-4 mb-3">Current Address</h4>

        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Country Code <span className="text-red">**</span></Form.Label>
              <Form.Select 
                name="currentAddress.countryCode" 
                value={addressData.currentAddress.countryCode} 
                onChange={handleChange}
                required
              >
                <option value="">Select Country</option>
                {dropdownData.countries.map((country) => (
                  <option key={country.countryCode} value={country.countryCode}>
                    {country.countryCode} - {country.countryName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Region Code</Form.Label>
              <Form.Select 
                name="currentAddress.regionCode" 
                value={addressData.currentAddress.regionCode} 
                onChange={handleChange}
                disabled={!addressData.currentAddress.countryCode}
              >
                <option value="">Select Region</option>
                {dropdownData.regions.map((region) => (
                  <option key={region.regionCode} value={`${region.regionCode} - ${region.regionName}`}>
                    {region.regionCode} - {region.regionName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>District Code</Form.Label>
              <Form.Select 
                name="currentAddress.districtCode" 
                value={addressData.currentAddress.districtCode} 
                onChange={handleChange}
                disabled={!addressData.currentAddress.regionCode}
              >
                <option value="">Select District</option>
                {dropdownData.districts.map((district) => (
                  <option key={district.code} value={`${district.code} - ${district.description}`}>
                    {district.code} - {district.description}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Tehsil Code</Form.Label>
              <Form.Select 
                name="currentAddress.tehsilCode" 
                value={addressData.currentAddress.tehsilCode} 
                onChange={handleChange}
                disabled={!addressData.currentAddress.districtCode}
              >
                <option value="">Select Tehsil</option>
                {dropdownData.tehsils.map((tehsil) => (
                  <option key={tehsil.code} value={`${tehsil.code} - ${tehsil.description}`}>
                    {tehsil.code} - {tehsil.description}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>City Code</Form.Label>
              <Form.Select 
                name="currentAddress.cityCode" 
                value={addressData.currentAddress.cityCode} 
                onChange={handleChange}
                disabled={!addressData.currentAddress.tehsilCode}
              >
                <option value="">Select City</option>
                {dropdownData.cities.map((city) => (
                  <option key={city.code} value={`${city.code} - ${city.description}`}>
                    {city.code} - {city.description}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Address 1</Form.Label>
              <Form.Control 
                type="text" 
                name="currentAddress.address1" 
                value={addressData.currentAddress.address1} 
                onChange={handleChange} 
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Address 2</Form.Label>
              <Form.Control 
                type="text" 
                name="currentAddress.address2" 
                value={addressData.currentAddress.address2} 
                onChange={handleChange} 
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Address 3</Form.Label>
              <Form.Control 
                type="text" 
                name="currentAddress.address3" 
                value={addressData.currentAddress.address3} 
                onChange={handleChange} 
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Check 
            type="checkbox"
            label="Is Permanent Address same as Current Address?"
            checked={isSameAddress}
            onChange={handleSameAddressChange}
          />
        </Form.Group>

        <h4 className="mt-4 mb-3">Permanent Address</h4>

        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Country Code</Form.Label>
              <Form.Select 
                name="permanentAddress.countryCode" 
                value={addressData.permanentAddress.countryCode} 
                onChange={handleChange}
                disabled={isSameAddress}
              >
                <option value="">Select Country</option>
                {dropdownData.countries.map((country) => (
                  <option key={country.countryCode} value={country.countryCode}>
                    {country.countryCode} - {country.countryName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Region Code</Form.Label>
              <Form.Select 
                name="permanentAddress.regionCode" 
                value={addressData.permanentAddress.regionCode} 
                onChange={handleChange}
                disabled={isSameAddress || !addressData.permanentAddress.countryCode}
              >
                <option value="">Select Region</option>
                {dropdownData.regions.map((region) => (
                  <option key={region.regionCode} value={`${region.regionCode} - ${region.regionName}`}>
                    {region.regionCode} - {region.regionName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>District Code</Form.Label>
              <Form.Select 
                name="permanentAddress.districtCode" 
                value={addressData.permanentAddress.districtCode} 
                onChange={handleChange}
                disabled={isSameAddress || !addressData.permanentAddress.regionCode}
              >
                <option value="">Select District</option>
                {dropdownData.districts.map((district) => (
                  <option key={district.code} value={`${district.code} - ${district.description}`}>
                    {district.code} - {district.description}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Tehsil Code</Form.Label>
              <Form.Select 
                name="permanentAddress.tehsilCode" 
                value={addressData.permanentAddress.tehsilCode} 
                onChange={handleChange}
                disabled={isSameAddress || !addressData.permanentAddress.districtCode}
              >
                <option value="">Select Tehsil</option>
                {dropdownData.tehsils.map((tehsil) => (
                  <option key={tehsil.code} value={`${tehsil.code} - ${tehsil.description}`}>
                    {tehsil.code} - {tehsil.description}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>City Code</Form.Label>
              <Form.Select 
                name="permanentAddress.cityCode" 
                value={addressData.permanentAddress.cityCode} 
                onChange={handleChange}
                disabled={isSameAddress || !addressData.permanentAddress.tehsilCode}
              >
                <option value="">Select City</option>
                {dropdownData.cities.map((city) => (
                  <option key={city.code} value={`${city.code} - ${city.description}`}>
                    {city.code} - {city.description}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Address 1</Form.Label>
              <Form.Control 
                type="text" 
                name="permanentAddress.address1" 
                value={addressData.permanentAddress.address1} 
                onChange={handleChange} 
                disabled={isSameAddress}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Address 2</Form.Label>
              <Form.Control 
                type="text" 
                name="permanentAddress.address2" 
                value={addressData.permanentAddress.address2} 
                onChange={handleChange} 
                disabled={isSameAddress}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Address 3</Form.Label>
              <Form.Control 
                type="text" 
                name="permanentAddress.address3" 
                value={addressData.permanentAddress.address3} 
                onChange={handleChange} 
                disabled={isSameAddress}
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="fields-btn-container">
          <Button variant="primary" type="submit" className="fields-submit" disabled={loading}>
            {loading ? 'Saving...' : 'Save Address Details'}
          </Button>
        </div>
      </Form>

      {loading && (
        <div className="loading-overlay">
          <Sample14 />
        </div>
      )}
    </div>
  );
};

export default AddressDetails;